<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center gap-4">
      <Select v-model="monthOrYear" :options="monthOrYearOptions" />
      <Datepicker
        v-if="monthOrYear === MonthOrYear.MONTH"
        v-model="month"
        month-picker
        :clearable="false"
      />
      <Datepicker v-else v-model="year" year-picker :clearable="false" />
      <CVButton outline :is-loading="isExporting" @click.prevent="handleDownloadCsv">
        {{ t('downloadCsv') }}
      </CVButton>
    </div>

    <div class="w-fit rounded-md border border-obsidian-200 bg-white p-4 shadow-sm">
      <p class="text-sm text-obsidian-500">
        {{ t('summary', { period: periodLabel, count: items.length }) }}
      </p>
      <p class="mt-2 text-lg font-semibold text-obsidian-800">
        {{ formatCurrency(writeOffs?.totalAmount ?? 0) }}
      </p>
    </div>

    <div :class="table({ class: 'grid-cols-[repeat(7,minmax(max-content,1fr))]' })">
      <div :class="tableHeader()">
        <div :class="tableCell()">{{ t('writtenOffOn') }}</div>
        <div :class="tableCell()">{{ t('legalEntity') }}</div>
        <div :class="tableCell()">{{ t('customer') }}</div>
        <div :class="tableCell()">{{ t('booking') }}</div>
        <div :class="tableCell({ numeric: true })">{{ t('amount') }}</div>
        <div :class="tableCell()">{{ t('taxProcedure') }}</div>
        <div :class="tableCell()">{{ t('taxCountry') }}</div>
      </div>
      <div v-for="item in items" :key="item.id" :class="tableRow()">
        <div :class="tableCell()">{{ formatDate(item.dueDate, DateTime.DATE_SHORT) }}</div>
        <div :class="tableCell()">{{ item.legalEntity.name }}</div>
        <div :class="tableCell()">{{ formatCustomerName(item) }}</div>
        <div :class="tableCell()">
          <router-link
            v-if="item.booking"
            :to="{ name: 'bookingEdit', params: { id: item.booking.id } }"
            class="text-link"
          >
            {{ item.booking.bookingNumber }}
          </router-link>
        </div>
        <div :class="tableCell({ numeric: true })">{{ formatCurrency(-item.amount / 100) }}</div>
        <div :class="tableCell()">{{ t(`taxProcedures.${item.taxProcedure}`) }}</div>
        <div :class="tableCell()">{{ item.taxCountry }}</div>
      </div>
    </div>
    <EntityListExceptions
      :status="status"
      :error="error"
      :items-count="items.length"
      :no-items-text="t('noWriteOffs')"
      small
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import CVButton from '@/components/buttons/CVButton.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import Select from '@/components/Select.vue';
import { formatDate } from '@/hooks/use-formatted-date';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';
import {
  useExportReceivablesWriteOffsCsv,
  useReceivablesWriteOffs,
} from '@/queries/use-receivables';
import { downloadBlob } from '@/utils/download-file';
import { addLeadingZeros, formatCurrency } from '@/utils/format-numbers';
import { table, tableCell, tableHeader, tableRow } from '@carvia/theme/lib/cva/grid';
import type { LedgerEntry } from '@carvia/ros-client-types';

const enum MonthOrYear {
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

// Write-offs are reconciled per accounting month, which follows the German calendar.
const ZONE = 'Europe/Berlin';

const { t } = useI18n();
const { legalEntityId, legalEntityOptions } = useLegalEntityFilter();

const previousMonth = DateTime.now().setZone(ZONE).minus({ month: 1 });
const month = ref({ month: previousMonth.month - 1, year: previousMonth.year });
const year = ref(previousMonth.year);
const monthOrYear = ref(MonthOrYear.MONTH);

const monthOrYearOptions = [
  { label: t('month'), value: MonthOrYear.MONTH },
  { label: t('year'), value: MonthOrYear.YEAR },
];

const period = computed(() => {
  const start =
    monthOrYear.value === MonthOrYear.MONTH
      ? DateTime.fromObject(
          { year: month.value.year, month: month.value.month + 1 },
          { zone: ZONE },
        ).startOf('month')
      : DateTime.fromObject({ year: year.value }, { zone: ZONE }).startOf('year');
  const end = monthOrYear.value === MonthOrYear.MONTH ? start.endOf('month') : start.endOf('year');
  return { start, end };
});

const periodLabel = computed(() =>
  monthOrYear.value === MonthOrYear.MONTH
    ? `${month.value.year}-${addLeadingZeros(month.value.month + 1, 2)}`
    : String(year.value),
);

const query = computed(() => ({
  dateAfter: period.value.start.toJSDate(),
  dateBefore: period.value.end.toJSDate(),
  legalEntityId: legalEntityId.value ?? undefined,
}));

const { data: writeOffs, status, error } = useReceivablesWriteOffs(query);
const items = computed(() => writeOffs.value?.items ?? []);

// Same naming as the receivables list: company, else full name.
const formatCustomerName = (item: LedgerEntry) => {
  const customer = item.booking?.customerData;
  return (
    customer?.company || [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || '-'
  );
};

const { mutateAsync: exportWriteOffsCsv, isPending: isExporting } =
  useExportReceivablesWriteOffsCsv();

const handleDownloadCsv = async () => {
  const csv = await exportWriteOffsCsv(query.value);
  const legalEntityName = legalEntityOptions.value.find(
    (option) => option.value === legalEntityId.value,
  )?.label;
  const prefix = legalEntityName ? `${legalEntityName.replace(/[^\p{L}\p{N}]+/gu, '_')}_` : '';
  await downloadBlob(
    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
    `${prefix}${t('writeOffsExport')}-${periodLabel.value}.csv`,
  );
};
</script>

<i18n lang="json">
{
  "en": {
    "amount": "Amount",
    "booking": "Booking",
    "customer": "Customer",
    "downloadCsv": "Download CSV",
    "legalEntity": "Legal entity",
    "month": "Month",
    "noWriteOffs": "No write-offs in this period",
    "summary": "Write-offs {period}: {count}",
    "taxCountry": "Tax country",
    "taxProcedure": "Tax procedure",
    "taxProcedures": {
      "REGULAR": "Regular",
      "REVERSE_CHARGE": "Reverse charge",
      "EXPORT": "Export"
    },
    "writeOffsExport": "write-offs-export",
    "writtenOffOn": "Written off on",
    "year": "Year"
  },
  "de": {
    "amount": "Betrag",
    "booking": "Buchung",
    "customer": "Kunde",
    "downloadCsv": "CSV runterladen",
    "legalEntity": "Gesellschaft",
    "month": "Monat",
    "noWriteOffs": "Keine Abschreibungen in diesem Zeitraum",
    "summary": "Abschreibungen {period}: {count}",
    "taxCountry": "Steuerland",
    "taxProcedure": "Steuerverfahren",
    "taxProcedures": {
      "REGULAR": "Regulär",
      "REVERSE_CHARGE": "Reverse Charge",
      "EXPORT": "Export"
    },
    "writeOffsExport": "abschreibungen-export",
    "writtenOffOn": "Abgeschrieben am",
    "year": "Jahr"
  },
  "es": {
    "amount": "Importe",
    "booking": "Reserva",
    "customer": "Cliente",
    "downloadCsv": "Descargar CSV",
    "legalEntity": "Sociedad",
    "month": "Mes",
    "noWriteOffs": "Sin cancelaciones en este periodo",
    "summary": "Cancelaciones {period}: {count}",
    "taxCountry": "País de IVA",
    "taxProcedure": "Régimen fiscal",
    "taxProcedures": {
      "REGULAR": "Normal",
      "REVERSE_CHARGE": "Inversión del sujeto pasivo",
      "EXPORT": "Exportación"
    },
    "writeOffsExport": "exportacion-cancelaciones",
    "writtenOffOn": "Cancelado el",
    "year": "Año"
  }
}
</i18n>
